import React from 'react';
import ReactDOM from 'react-dom/client';
import { ThemeProvider as MuiThemeProvider, createTheme, alpha } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { HelmetProvider } from 'react-helmet-async';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { ThemeProvider, useThemeContext } from './contexts/ThemeContext';
import { CookieProvider } from './contexts/CookieContext';
import { FavoritesProvider } from './contexts/FavoritesContext';

const createAppTheme = (isDarkMode: boolean) => createTheme({
  palette: {
    mode: isDarkMode ? 'dark' : 'light',
    primary: {
      main: '#00d4ff',
      light: '#5ce1ff',
      dark: '#0094b3',
    },
    secondary: {
      main: '#a855f7',
      light: '#c084fc',
      dark: '#7c3aed',
    },
    background: {
      default: isDarkMode ? '#07080a' : '#f0f4f8',
      paper: isDarkMode ? '#0d1117' : '#ffffff',
    },
    text: {
      primary: isDarkMode ? '#e6edf3' : '#1a202c',
      secondary: isDarkMode ? '#8b949e' : '#64748b',
    },
    divider: isDarkMode ? 'rgba(48, 54, 61, 0.8)' : 'rgba(0, 0, 0, 0.08)',
    error: { main: '#f85149' },
    warning: { main: '#f0883e' },
    success: { main: '#3fb950' },
    info: { main: '#58a6ff' },
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    h1: { fontWeight: 800, letterSpacing: '-0.025em' },
    h2: { fontWeight: 700, letterSpacing: '-0.025em' },
    h3: { fontWeight: 700, letterSpacing: '-0.02em' },
    h4: { fontWeight: 700, letterSpacing: '-0.015em' },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
    button: { fontWeight: 600, letterSpacing: '0.01em' },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundImage: isDarkMode
            ? 'radial-gradient(ellipse 80% 50% at 50% -20%, rgba(0, 212, 255, 0.08), transparent)'
            : 'none',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          backgroundColor: isDarkMode ? 'rgba(13, 17, 23, 0.8)' : '#ffffff',
          backdropFilter: isDarkMode ? 'blur(20px)' : 'none',
          border: isDarkMode ? '1px solid rgba(48, 54, 61, 0.6)' : '1px solid rgba(0, 0, 0, 0.08)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            borderColor: isDarkMode ? 'rgba(0, 212, 255, 0.3)' : 'rgba(0, 212, 255, 0.5)',
            boxShadow: isDarkMode
              ? '0 8px 32px rgba(0, 212, 255, 0.1), 0 0 0 1px rgba(0, 212, 255, 0.1)'
              : '0 8px 32px rgba(0, 0, 0, 0.1)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: isDarkMode ? '#0d1117' : '#ffffff',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            backgroundColor: isDarkMode ? 'rgba(13, 17, 23, 0.6)' : 'rgba(0, 0, 0, 0.02)',
            backdropFilter: isDarkMode ? 'blur(10px)' : 'none',
            '& fieldset': {
              borderColor: isDarkMode ? 'rgba(48, 54, 61, 0.8)' : 'rgba(0, 0, 0, 0.1)',
            },
            '&:hover fieldset': {
              borderColor: isDarkMode ? 'rgba(0, 212, 255, 0.3)' : 'rgba(0, 212, 255, 0.5)',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#00d4ff',
              borderWidth: '1px',
              boxShadow: '0 0 0 3px rgba(0, 212, 255, 0.1)',
            },
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          textTransform: 'none' as const,
          fontWeight: 600,
          transition: 'all 0.2s ease',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: isDarkMode
              ? '0 4px 20px rgba(0, 212, 255, 0.25)'
              : '0 4px 20px rgba(0, 0, 0, 0.15)',
          },
        },
        outlined: {
          borderColor: isDarkMode ? 'rgba(48, 54, 61, 0.8)' : 'rgba(0, 0, 0, 0.12)',
          '&:hover': {
            borderColor: '#00d4ff',
            backgroundColor: 'rgba(0, 212, 255, 0.05)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
        },
        outlined: {
          borderColor: isDarkMode ? 'rgba(48, 54, 61, 0.8)' : 'rgba(0, 0, 0, 0.12)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: isDarkMode ? 'rgba(13, 17, 23, 0.95)' : '#ffffff',
          backdropFilter: isDarkMode ? 'blur(20px)' : 'none',
          borderRight: isDarkMode ? '1px solid rgba(48, 54, 61, 0.5)' : '1px solid rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: isDarkMode ? 'rgba(7, 8, 10, 0.85)' : alpha('#ffffff', 0.9),
          backdropFilter: 'blur(20px)',
          borderBottom: isDarkMode ? '1px solid rgba(48, 54, 61, 0.5)' : '1px solid rgba(0, 0, 0, 0.08)',
          boxShadow: 'none',
          color: isDarkMode ? '#e6edf3' : '#1a202c',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: isDarkMode ? 'rgba(13, 17, 23, 0.95)' : '#ffffff',
          backdropFilter: isDarkMode ? 'blur(24px)' : 'none',
          border: isDarkMode ? '1px solid rgba(48, 54, 61, 0.6)' : '1px solid rgba(0, 0, 0, 0.08)',
          borderRadius: 16,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: isDarkMode ? 'rgba(22, 27, 34, 0.95)' : 'rgba(30, 30, 30, 0.95)',
          backdropFilter: 'blur(12px)',
          borderRadius: 8,
          border: '1px solid rgba(48, 54, 61, 0.4)',
          fontSize: '0.8rem',
          fontWeight: 500,
          padding: '6px 12px',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: isDarkMode ? 'rgba(48, 54, 61, 0.5)' : 'rgba(0, 0, 0, 0.06)',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '2px 8px',
          '&:hover': {
            backgroundColor: isDarkMode ? 'rgba(0, 212, 255, 0.06)' : 'rgba(0, 212, 255, 0.04)',
          },
          '&.Mui-selected': {
            backgroundColor: isDarkMode ? 'rgba(0, 212, 255, 0.1)' : 'rgba(0, 212, 255, 0.08)',
            '&:hover': {
              backgroundColor: isDarkMode ? 'rgba(0, 212, 255, 0.15)' : 'rgba(0, 212, 255, 0.12)',
            },
          },
        },
      },
    },
  },
});

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

function AppWithTheme() {
  const { isDarkMode } = useThemeContext();
  const theme = createAppTheme(isDarkMode);

  React.useEffect(() => {
    document.body.setAttribute('data-mui-color-scheme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      <App />
    </MuiThemeProvider>
  );
}

root.render(
  <React.StrictMode>
    <HelmetProvider>
      <CookieProvider>
        <ThemeProvider>
          <FavoritesProvider>
            <AppWithTheme />
          </FavoritesProvider>
        </ThemeProvider>
      </CookieProvider>
    </HelmetProvider>
  </React.StrictMode>
);

reportWebVitals();
